import {WonderlandEngine, Emitter} from '@wonderlandengine/api';
import {Howl} from 'howler';
import {GA} from '../utils/ga-utils.js';
import {SoundManagerInstance, Sounds} from './soundmanager.js';

export enum SceneNames {
    Scene0 = 'DeadSecretCircle_Scene0Intro.bin', // Unity: Start.scene
    Scene1 = 'DeadSecretCircle_Scene1.bin', // Unity: IntroScene.scene
    Scene2Opening = 'DeadSecretCircle_Scene2Opening.bin', // Unity: Opening.scene
    Scene2 = 'DeadSecretCircle_Scene2.bin', // Unity: PatriciaIntro.scene
    SceneCallToAction = 'DeadSecretCircle_CallToAction.bin',
}

class GameManager {
    onSceneReady: Emitter<SceneNames[]> = new Emitter();
    onPreloading: Emitter = new Emitter();
    private isInitialized = false;
    private engine?: WonderlandEngine;
    private currentLoadingScene: SceneNames = SceneNames.Scene0;
    private currentAmbient?: Howl;
    private buffer: ArrayBuffer = new ArrayBuffer(0);
    private isLoading = false;
    private preloadedScenes: string[] = [];
    private preloadTries = 0;

    initialize(engine: WonderlandEngine) {
        if (this.isInitialized) {
            console.warn('GameManager already initialized!');
            return;
        }

        this.isInitialized = true;

        this.engine = engine;
    }

    preloaded(scene: string) {
        this.preloadedScenes.push(scene);
    }

    loadScene(scene: SceneNames) {
        if (!this.isInitialized || !this.engine) {
            return;
        }

        if (this.currentAmbient != null) {
            this.currentAmbient.stop();
        }

        this.currentLoadingScene = scene;

        if (scene === SceneNames.Scene1) {
            const interval = setInterval(() => {
                if (
                    this.preloadedScenes.includes('DeadSecretCircle_Scene1.bin') &&
                    this.preloadedScenes.includes('DeadSecretCircle_Scene1-textures.bin')
                ) {
                    clearInterval(interval);
                    GA.sendPreloadDoneEvent(this.preloadTries / 2);
                    this.engine!.loadMainScene(scene)
                        .then((s) => {
                            this.onSceneLoaded();
                        })
                        .catch((e) => {
                            console.error('Failed to load scene ' + scene + ': ' + e);
                        });
                } else {
                    this.preloadTries++;
                    if (this.preloadTries == 6) {
                        GA.sendPreloadStartedEvent();
                        this.onPreloading.notify();
                    }
                }
            }, 500);
        } else {
            this.engine
                .loadMainScene(scene)
                .then((s) => {
                    this.onSceneLoaded();
                })
                .catch((e) => {
                    console.error('Failed to load scene ' + scene + ': ' + e);
                });
        }
    }

    stopAmbient() {
        if (this.currentAmbient != null) {
            this.currentAmbient.stop();
        }
    }

    private onSceneLoaded() {
        switch (this.currentLoadingScene) {
            case SceneNames.Scene0:
                this.currentAmbient = new Howl({
                    src: ['music/mus_title.mp3'],
                    loop: true,
                    volume: 0.5,
                });
                this.currentAmbient.play();

                SoundManagerInstance.initialize([
                    Sounds.elevatorStart,
                    Sounds.elevatorLoop,
                    Sounds.boom1,
                    Sounds.boom4,
                ]);
                break;
            case SceneNames.Scene1:
                GA.sendProgressEvent('menu');
                this.currentAmbient = new Howl({
                    src: ['ambient/amb_dungeon.mp3'],
                    loop: true,
                });
                this.currentAmbient.play();
                SoundManagerInstance.initialize();
                break;
            case SceneNames.Scene2Opening:
                GA.sendProgressEvent('nightmare');
                this.currentAmbient = new Howl({
                    src: ['music/mus_newarea_investigation.mp3'],
                    volume: 0.25,
                    loop: false,
                });
                this.currentAmbient.play();
                break;
            case SceneNames.Scene2:
                GA.sendProgressEvent('story');
                break;
            case SceneNames.SceneCallToAction:
                GA.sendProgressEvent('call to action');
                this.currentAmbient = new Howl({
                    src: ['music/mus_title.mp3'],
                    loop: true,
                    volume: 0.5,
                });
                this.currentAmbient.play();
                break;
        }

        this.onSceneReady.notify(this.currentLoadingScene);
    }
}

export const GameManagerInstance = new GameManager();
